/**
 * Sign In Page
 *
 * Allows users to sign in with email/password or Google OAuth.
 * Redirects to /dashboard on successful authentication.
 * Redirects already authenticated users to /dashboard.
 */

import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AuthForm from '@components/AuthForm';
import Header from '@components/Header';
import {
  signInWithEmail,
  signInWithGoogle,
  resetPasswordForEmail,
  isAuthenticated,
} from '@lib/auth';

export default function SignIn() {
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if already authenticated
    const checkAuth = async () => {
      const authenticated = await isAuthenticated();
      if (authenticated) {
        navigate('/dashboard', { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  const handleSignIn = async (email: string, password: string) => {
    await signInWithEmail(email, password);
    navigate('/dashboard', { replace: true });
  };

  const handleGoogleSignIn = async () => {
    await signInWithGoogle();
    // Redirect is handled by Supabase OAuth flow
  };

  const handleForgotPassword = async (email: string) => {
    await resetPasswordForEmail(email);
  };

  return (
    <>
      <Header />
      <div className="animate-fade-in flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-100 via-teal-50 to-cyan-100 px-4 py-12">
        <div className="w-full max-w-md pt-16">
          <div className="animate-slide-up rounded-2xl bg-white p-8 shadow-xl">
            {/* Header */}
            <div className="mb-8 text-center">
              <h1 className="mb-2 text-3xl font-bold text-gray-900">
                Welcome Back
              </h1>
              <p className="text-gray-600">
                Sign in to continue your breathing journey
              </p>
            </div>

            {/* Tabs */}
            <div className="mb-8 flex gap-2 rounded-lg bg-gray-100 p-1">
              <div className="flex-1 rounded-md bg-white px-4 py-2 text-center font-medium text-gray-900 shadow-sm">
                Sign In
              </div>
              <Link
                to="/signup"
                className="flex-1 rounded-md px-4 py-2 text-center font-medium text-gray-600 transition-colors hover:text-gray-900"
              >
                Sign Up
              </Link>
            </div>

            {/* Auth Form */}
            <AuthForm
              mode="signin"
              onSubmit={handleSignIn}
              onGoogleSignIn={handleGoogleSignIn}
              onForgotPassword={handleForgotPassword}
            />

            {/* Footer Note */}
            <p className="mt-8 text-center text-xs text-gray-500">
              By signing in, you agree to Xhalin's{' '}
              <a href="/terms" className="text-indigo-600 hover:underline">
                Terms of Use
              </a>
            </p>
          </div>

          {/* Additional Links */}
          <div className="mt-6 text-center">
            <Link
              to="/"
              className="text-sm text-gray-600 transition-colors hover:text-gray-900"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
